import {
  makeStyles,
  Title2,
  TableColumnDefinition,
  createTableColumn,
  Table,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  useTableFeatures,
  TableHeader,
  useTableColumnSizing_unstable,
  TableColumnSizingOptions,
  Checkbox,
  tokens,
  Button,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuButton,
  MenuPopover,
  Tooltip,
  Title3,
  Toaster,
  useToastController,
  useId,
  Toast,
  ToastTitle,
} from "@fluentui/react-components";
import { useNavigate } from "react-router";
import { useMemo, useState, useEffect } from "react";
import CommandBar from "../components/CommandBar";
import { apiUrl } from "../utils/api";
import { ContactModesDialog } from "../components/ContactModesDialog";
import { contactTypeLabel } from "../utils/contactTypeLabel";
import { mitVerantwortungVon } from "../utils/verantwortungErben";
import { Call20Regular, Mail20Regular, ChevronDown20Regular, ClockBill20Regular, Delete20Regular } from "@fluentui/react-icons";
import { ContactDetailsFormValues } from "../types/Contact";
import { HouseholdOrgChart } from "../components/HouseholdOrgChart";
import { HouseholdDialog, HouseholdMember } from "../components/HouseholdDialog";
import { CompanyMemberDialog, CompanyMember } from "../components/CompanyMemberDialog";
import { RelationshipDialog, RelationshipItem } from "../components/RelationshipDialog";

const useStyles = makeStyles({
  labelSection: {
    fontWeight: 600,
    fontSize: "18px",
    marginTop: "24px",
    gridColumn: "1 / span 2",
    margin: "24px 0 8px 0",
  },
  stripedRow: {
    backgroundColor: "#f2f2f2",
  },
  cellEllipsis: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  orgSection: {
    marginTop: "32px",
    marginBottom: "32px",
  },
  orgContainer: {
    width: "100%",
    overflowX: "auto",
    padding: "8px 0 16px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "8px",
    marginTop: "16px",
  },
  contactCell: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    fontSize: "12px",
  }
});

interface ansprechpartner {
  id?: number;
  kontaktId?: number;
  name?: string;
  rolle?: string; // Relation types (Gesellschafter, Mitarbeiter, etc.)
  funktion?: string[]; // Contact roles (from dropdown)
  telefon?: string;
  email?: string;
  anteile?: string;
  erreichbarkeit?: string;
  // For ContactModesDialog
  displayName?: string;
  phones?: any[];
  emails?: any[];
  typ?: string;
  relatedCommunications?: any[];
  isExtern?: boolean;
  employer?: string;
  // Raw fields for Dialogs
  lastname?: string;
  firstname?: string;
  birthdate?: string;
}

interface OrganisationDetailsProps {
  kontaktId?: number;
  relations?: any[];
  contactType?: string;
  group?: { id: number; name: string; type: string; contacts?: any[]; ansprechpartnerId?: number };
  /** Called after a relationship was created/removed so the parent can refetch. */
  onChanged?: () => void;
}

export const RelationshipDetails: React.FC<OrganisationDetailsProps> = (props) => {
  /* Lint Fix: relations defaults to [] in destructuring or check */
  const { kontaktId, relations = [], contactType, group, onChanged } = props;
  const navigate = useNavigate();
  const styles = useStyles();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    mode: "phone" | "email";
    contact: ContactDetailsFormValues | null;
  }>({
    open: false,
    mode: "phone",
    contact: null,
  });

  const [showHouseholdDialog, setShowHouseholdDialog] = useState(false);
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [showRelationshipDialog, setShowRelationshipDialog] = useState(false);
  const [viewMode, setViewMode] = useState<'tabelle' | 'diagramm'>('tabelle');

  /* Toast feedback for relationship mutations */
  const toasterId = useId("rel-toaster");
  const { dispatchToast } = useToastController(toasterId);
  const showToast = (intent: "success" | "error", message: string) =>
    dispatchToast(
      <Toast>
        <ToastTitle>{message}</ToastTitle>
      </Toast>,
      { intent },
    );

  // 1. Household Members (Only if Person & Group exists)
  const householdMembers = useMemo(() => {
    if (contactType === 'Person' && group && group.contacts) {
      return group.contacts.map((member: any) => {
        const id = member.id;
        // Find relation from "Me" (kontaktId) to "Member" (id)
        const rel = relations.find(r => r.contactDest?.id === id);
        const isSelf = id === kontaktId;
        let roleName = rel?.relationType?.contactRelationType || "";
        if (isSelf) roleName = "Ich";

        const phoneComms = member.communications || [];
        const phone = phoneComms.find((c: any) => c.type === 'phone' || c.type === 'mobile' || c.type === 'fax')?.value || "";
        const email = phoneComms.find((c: any) => c.type === 'email')?.value || "";

        const phones = phoneComms.filter((c: any) => c.type === 'phone' || c.type === 'mobile' || c.type === 'fax').map((c: any) => ({
          ...c, number: c.value
        }));
        const emails = phoneComms.filter((c: any) => c.type === 'email').map((c: any) => ({
          ...c, address: c.value
        }));

        const name = member.displayName || [member.lastname, member.firstName].filter(Boolean).join(", ");
        const contactRoles = Array.isArray(member.roles)
          ? member.roles.map((r: any) => r.roleDefinition?.contactRole || r.contactRole).filter(Boolean)
          : [];

        if (rel?.function) {
          const funcs = rel.function.split(',').map((s: string) => s.trim());
          contactRoles.push(...funcs);
        }

        return {
          id: id,
          kontaktId: id,
          name: name,
          rolle: roleName,
          funktion: contactRoles,
          telefon: phone,
          email: email,
          erreichbarkeit: member.erreichbarkeit || "",
          anteile: rel?.share || "",
          phones: phones,
          emails: emails,
          typ: "Person",
          relatedCommunications: [],
          isExtern: false,
          employer: "",
          lastname: member.lastname,
          firstname: member.firstName,
          birthdate: member.birthDate,
        } as ansprechpartner;
      });
    }
    return [];
  }, [group, contactType, relations, kontaktId]);

  // 2. Connected Contacts (Relations filtered by Household)
  const connectedContacts = useMemo(() => {
    const householdIds = new Set(householdMembers.map(m => m.id));
    const map = new Map<number, ansprechpartner>();

    relations.forEach((rel) => {
      const dest = rel.contactDest;
      if (!dest) return;
      if (householdIds.has(dest.id)) return; // Skip household members

      const id = dest.id;
      const roleName = rel.relationType?.contactRelationType || "";

      // For Person view, we might show company relations too (User request: "Verknüpfungen" table)
      // Earlier filter was: if (contactType === 'Person') { if (destType !== 'Person') return; }
      // BUT current request is to show "Inhaber von Courte KG" which is a Company.
      // So we SHOULD allow non-persons here if it's a relation.

      if (!map.has(id)) {
        const phoneComms = dest.communications || [];
        const phone = phoneComms.find((c: any) => c.type === 'phone' || c.type === 'mobile' || c.type === 'fax')?.value || "";
        const email = phoneComms.find((c: any) => c.type === 'email')?.value || "";

        const phones = phoneComms.filter((c: any) => c.type === 'phone' || c.type === 'mobile' || c.type === 'fax').map((c: any) => ({
          ...c, number: c.value
        }));
        const emails = phoneComms.filter((c: any) => c.type === 'email').map((c: any) => ({
          ...c, address: c.value
        }));

        const name = dest.displayName || [dest.lastname, dest.firstName].filter(Boolean).join(", ");
        const isExtern = rel.isExtern;
        const employer = rel.externalCompany?.displayName || [rel.externalCompany?.lastname, rel.externalCompany?.firstName].filter(Boolean).join(", ");

        const contactRoles = Array.isArray(dest.roles)
          ? dest.roles.map((r: any) => r.roleDefinition?.contactRole || r.contactRole).filter(Boolean)
          : [];

        // FIlter out "Gesellschaft" as requested
        const filteredRoles = contactRoles.filter((r: string) => r !== "Gesellschaft" && r !== "Gesellschafter");

        if (rel.function) {
          const funcs = rel.function.split(',').map((s: string) => s.trim());
          filteredRoles.push(...funcs);
        }

        map.set(id, {
          id: id,
          kontaktId: id,
          name: name,
          displayName: name,
          rolle: roleName,
          funktion: filteredRoles,
          telefon: phone,
          email: email,
          erreichbarkeit: dest.erreichbarkeit || "",
          anteile: rel.share || "",
          phones: phones,
          emails: emails,
          typ: dest.type?.type || "Person",
          relatedCommunications: [],
          isExtern: isExtern,
          employer: employer,
          lastname: dest.lastname,
          firstname: dest.firstName,
          birthdate: dest.birthDate // Assuming birthDate might exist on dest too
        });
      } else {
        const existing = map.get(id)!;
        if (existing.rolle && !existing.rolle.includes(roleName)) {
          existing.rolle += `, ${roleName}`;
        }
      }
    });

    return Array.from(map.values());
  }, [relations, householdMembers]);

  // Kundenverbindung bin (ContactGroup of type 'Kundenverbindung') — shown for
  // any contact type (Firma or Person), unlike the Haushalt table.
  const kundenverbindung =
    group && group.type === 'Kundenverbindung' ? group : undefined;
  // Members are lazy-loaded on first expand (a bin can have hundreds of
  // members — don't pull them on every contact open).
  const [kvOpen, setKvOpen] = useState(false);
  const [kvMembers, setKvMembers] = useState<
    { id: number; name: string; typ: string }[] | null
  >(null);
  const [kvLoading, setKvLoading] = useState(false);
  useEffect(() => {
    if (!kvOpen || !kundenverbindung || kvMembers !== null || kvLoading) return;
    setKvLoading(true);
    fetch(apiUrl(`contactgroup/${kundenverbindung.id}/contacts`))
      .then((r) => (r.ok ? r.json() : []))
      .then((data: any) => setKvMembers(Array.isArray(data) ? data : []))
      .catch(() => setKvMembers([]))
      .finally(() => setKvLoading(false));
  }, [kvOpen, kundenverbindung, kvMembers, kvLoading]);


  // Columns definition
  const columns = useMemo(() => {
    const isPrivate = contactType === 'Person';
    const allColumns = [
      createTableColumn<ansprechpartner>({
        columnId: "select",
        renderHeaderCell: () => <></>,
      }),
      createTableColumn<ansprechpartner>({
        columnId: "Name",
        renderHeaderCell: () => <>Name</>,
        renderCell: (item) => <>{item.name}</>,
      }),
      createTableColumn<ansprechpartner>({
        columnId: "Rolle",
        renderHeaderCell: () => <>Rolle</>,
        renderCell: (item) => <div className={styles.cellEllipsis} title={item.rolle}>{item.rolle}</div>,
      }),
      createTableColumn<ansprechpartner>({
        columnId: "Funktion",
        renderHeaderCell: () => <>Funktion</>,
        renderCell: (item) => {
          const roles = item.funktion || [];
          if (roles.length <= 1) {
            return <>{roles[0] || ""}</>;
          }
          const [first, ...rest] = roles;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>{first}</span>
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Button appearance="transparent" icon={<ChevronDown20Regular />} size="small" />
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    {rest.map((r, idx) => (
                      <MenuItem key={idx}>{r}</MenuItem>
                    ))}
                  </MenuList>
                </MenuPopover>
              </Menu>
            </div>
          )
        },
      }),
      createTableColumn<ansprechpartner>({
        columnId: "Kontaktmöglichkeit",
        renderHeaderCell: () => <>Kontaktmöglichkeit</>,
        renderCell: (item) => (
          <div style={{ display: "flex", gap: "8px" }} >
            <Button
              icon={<Call20Regular />}
              appearance="subtle"
              onClick={(e) => {
                e.stopPropagation();
                setDialogState({ open: true, mode: "phone", contact: item as any });
              }}
            />
            <Button
              icon={<Mail20Regular />}
              appearance="subtle"
              onClick={(e) => {
                e.stopPropagation();
                setDialogState({ open: true, mode: "email", contact: item as any });
              }}
            />
            {item.erreichbarkeit && (
              <Tooltip content={item.erreichbarkeit} relationship="label">
                <Button
                  icon={<ClockBill20Regular />}
                  appearance="subtle"
                  disabled // Optional: or make it open something? For now just display tooltip
                  style={{ color: tokens.colorNeutralForeground3 }} // Optional styling
                />
              </Tooltip>
            )}
          </div >
        ),
      }),
      createTableColumn<ansprechpartner>({
        columnId: "Anteile",
        renderHeaderCell: () => <>Verbindungsnotiz</>,
        renderCell: (item) => <>{item.anteile}</>,
      }),
    ];

    if (isPrivate) {
      // For private/Person contacts, filter columns
      const allowedIds = ["select", "Name", "Rolle", "Funktion"];
      return allColumns.filter(c => allowedIds.includes(c.columnId as string));
    }
    return allColumns;
  }, [styles, contactType]);

  // Columns for "Verknüpfungen" (Connections) - Specific Order: Rolle, Funktion, Name (Unternehmen)
  const columnsConnections = useMemo(() => {
    // We want: Select, Rolle, Funktion, Name, Kontaktmöglichkeit...
    // Base columns from `columns` memo, but reordered.
    // Map columnId to object
    const colMap = new Map(columns.map(c => [c.columnId, c]));

    const order = ["select", "Name", "Rolle", "Funktion", "Anschrift", "Verbindungsnotiz"];

    return order.map(id => colMap.get(id)).filter(Boolean) as TableColumnDefinition<ansprechpartner>[];
  }, [columns]);

  const handleEdit = (item: ansprechpartner) => {
    navigate(`/kontakte/edit/${item.kontaktId}`, { state: { k: item } });
  };

  const columnSizingOptions: TableColumnSizingOptions = useMemo(
    () => ({
      select: { defaultWidth: 48, minWidth: 48 },
      Name: { defaultWidth: 200, minWidth: 120 },
      Rolle: { defaultWidth: 150, minWidth: 100 },
      Funktion: { defaultWidth: 150, minWidth: 120 },
      "Kontaktmöglichkeit": { defaultWidth: 150, minWidth: 100 },
      Anteile: { defaultWidth: 80, minWidth: 60 },
      Extern: { defaultWidth: 80, minWidth: 60 },
      Unternehmen: { defaultWidth: 200, minWidth: 150 },
    }),
    [],
  );

  /* Table Logic */
  const personRoles = ["Mitarbeiter", "Inhaber", "Ansprechpartner", "Geschäftsführer", "Komplementär"];
  const isPersonRole = (role: string) => personRoles.some(r => role.includes(r));

  const primaryData = contactType === 'Person'
    ? householdMembers
    : connectedContacts.filter(c => c.typ === 'Person' || isPersonRole(c.rolle || ""));

  const secondaryData = contactType === 'Person'
    ? connectedContacts
    : connectedContacts.filter(c => c.typ !== 'Person' && !isPersonRole(c.rolle || ""));

  const { getRows: getRows1, tableRef: tableRef1, columnSizing_unstable: sizing1 } = useTableFeatures(
    { columns: columns, items: primaryData },
    [useTableColumnSizing_unstable({ columnSizingOptions })],
  );

  const { getRows: getRows2, tableRef: tableRef2, columnSizing_unstable: sizing2 } = useTableFeatures(
    { columns: columnsConnections, items: secondaryData },
    [useTableColumnSizing_unstable({ columnSizingOptions })],
  );

  const rows1 = getRows1();
  const rows2 = getRows2();

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  /* ---- Persistence: create-if-new contact + link via /contact-relation ----
     Replaces the old MultiStepForm queue, which collected form data but never
     saved anything. Adding a relationship now creates a real contact (when the
     dialog didn't pick an existing one) and writes a ContactRelation row. */

  type RelInput = {
    type: "Person" | "Firma";
    lastname: string;
    firstname?: string;
    role: string;
    function?: string;
    share?: string;
    contactId?: number;
    birthdate?: string;
  };

  // Firma = ContactType id 1 (seeded). The Person id is resolved lazily from
  // GET /contacttype so it isn't hardcoded; `type` is nullable, so a missing
  // id still yields a usable Person contact.
  const resolvePersonTypeId = async (): Promise<number | undefined> => {
    try {
      const res = await fetch(apiUrl("contacttype"));
      const list = await res.json();
      const hit = Array.isArray(list)
        ? list.find((t: any) => String(t?.type).toLowerCase() === "person")
        : null;
      return hit?.id;
    } catch {
      return undefined;
    }
  };

  // Create a real contact (same POST /contact the create-contact flow uses)
  // and return its new id, or null on failure.
  const createContactForRelation = async (rel: RelInput): Promise<number | null> => {
    const body: Record<string, unknown> = {
      lastname: rel.lastname,
      ...(rel.firstname ? { firstName: rel.firstname } : {}),
      ...(rel.type === "Firma" ? { displayName: rel.lastname } : {}),
      ...(rel.birthdate ? { birthDate: rel.birthdate } : {}),
    };
    if (rel.type === "Firma") {
      body.type = { id: 1, type: "Firma" };
    } else {
      const personTypeId = await resolvePersonTypeId();
      if (personTypeId) body.type = { id: personTypeId };
    }
    // T-6: Aus einer Firma heraus angelegt → FO/BO der Firma erben (der
    // Server prüft Mandant und Lesbarkeit). Von einer Person aus nicht.
    const payload = contactType && contactType !== "Person" ? mitVerantwortungVon(body, kontaktId) : body;
    try {
      const res = await fetch(apiUrl("contact"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) return null;
      const data = await res.json().catch(() => null);
      const newId = data?.id ?? data?.contact?.id;
      return typeof newId === "number" ? newId : null;
    } catch {
      return null;
    }
  };

  // Link an existing (searched) or freshly created contact to this contact.
  const persistRelation = async (rel: RelInput): Promise<boolean> => {
    if (!kontaktId) return false;
    let destId = rel.contactId ?? null;
    if (!destId) destId = await createContactForRelation(rel);
    if (!destId) return false;
    try {
      const res = await fetch(apiUrl("contact-relation"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contactSrcId: kontaktId,
          contactDestId: destId,
          relationType: rel.role,
          ...(rel.function ? { function: rel.function } : {}),
          ...(rel.share ? { share: rel.share } : {}),
        }),
      });
      return res.ok;
    } catch {
      return false;
    }
  };

  const persistMany = async (rels: RelInput[]) => {
    const valid = rels.filter((r) => r.lastname && r.role);
    if (valid.length === 0) return;
    let ok = 0;
    for (const r of valid) {
      if (await persistRelation(r)) ok += 1;
    }
    if (ok > 0) {
      showToast("success", `${ok} Verbindung${ok === 1 ? "" : "en"} gespeichert`);
      onChanged?.();
    }
    if (ok < valid.length) {
      const failed = valid.length - ok;
      showToast("error", `${failed} Verbindung${failed === 1 ? "" : "en"} fehlgeschlagen`);
    }
  };

  const handleHouseholdConfirm = (members: HouseholdMember[]) => {
    setShowHouseholdDialog(false);
    persistMany(
      members.map((member) => ({
        type: "Person",
        lastname: member.lastname,
        firstname: member.firstname,
        role: (member.role as string) || "",
        birthdate: member.birthdate,
      })),
    );
  };

  const handleCompanyMemberConfirm = (members: CompanyMember[]) => {
    setShowCompanyDialog(false);
    persistMany(
      members.map((member) => ({
        type: "Person",
        lastname: member.lastname,
        firstname: member.firstname,
        role: member.role,
        function: member.function,
        share: member.shares,
      })),
    );
  };

  const handleRelationshipConfirm = (items: RelationshipItem[]) => {
    setShowRelationshipDialog(false);
    persistMany(
      items.map((item) => ({
        type: item.type,
        lastname: item.lastname,
        firstname: item.firstname,
        role: item.role,
        function: item.function,
        share: item.shares,
        contactId: item.contactId,
      })),
    );
  };

  // Remove ALL relations between this contact and the row's contact
  // (DELETE /contact-relation/between/:a/:b), then refresh.
  const handleRemoveRelation = async (destContactId?: number) => {
    if (!kontaktId || !destContactId) return;
    try {
      const res = await fetch(
        apiUrl(`contact-relation/between/${kontaktId}/${destContactId}`),
        { method: "DELETE" },
      );
      if (res.ok) {
        showToast("success", "Verbindung entfernt");
        onChanged?.();
      } else {
        showToast("error", "Entfernen fehlgeschlagen");
      }
    } catch {
      showToast("error", "Entfernen fehlgeschlagen");
    }
  };

  return (
    <>
      <ContactModesDialog
        open={dialogState.open}
        contact={dialogState.contact}
        mode={dialogState.mode}
        onOpenChange={(open) => setDialogState((prev) => ({ ...prev, open }))}
      />

      <HouseholdDialog
        open={showHouseholdDialog}
        onClose={() => setShowHouseholdDialog(false)}
        onConfirm={handleHouseholdConfirm}
      />

      <CompanyMemberDialog
        open={showCompanyDialog}
        onClose={() => setShowCompanyDialog(false)}
        onConfirm={handleCompanyMemberConfirm}
      />

      <RelationshipDialog
        open={showRelationshipDialog}
        onClose={() => setShowRelationshipDialog(false)}
        onConfirm={handleRelationshipConfirm}
      />

      <Toaster toasterId={toasterId} />


      {/* Household Org Chart (Only for Person) */}
      {/* Household Org Chart removed (integrated into view toggle below) */}

      {/* Table 1: Rolle im Unternehmen or Rolle im Haushalt */}
      {/* Header with View Toggle for Household */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, marginTop: 32 }}>
        {contactType === 'Person' && group?.type === 'Haushalt' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Title2>Rolle im Haushalt</Title2>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button appearance="transparent" icon={<ChevronDown20Regular />} iconPosition="after">
                  {viewMode === 'tabelle' ? "Tabelle" : "Haushaltsdiagramm"}
                </Button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem onClick={() => setViewMode('tabelle')}>Tabelle</MenuItem>
                  <MenuItem onClick={() => setViewMode('diagramm')}>Haushaltsdiagramm</MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </div>
        ) : (
          <Title2>{contactType === 'Person' ? "Haushalt" : "Verantwortlichkeiten und Zuständigkeiten fürs Unternehmen"}</Title2>
        )}
      </div>

      {viewMode === 'diagramm' && contactType === 'Person' && group?.type === 'Haushalt' ? (
        <div className={styles.orgContainer}>
          <HouseholdOrgChart group={group} currentContactId={kontaktId} relations={relations} />
        </div>
      ) : (
        <div style={{ fontSize: "14px", width: "100%", overflowX: "auto" }}>
          <CommandBar
            header="Suche"
            onSearchChange={() => console.log("...")}
            onEditClick={() => console.log("...")}
            onNewClick={() => {
              // der comment unten ist nicht korrekt wenn das kein haushalt ist oder hat muss einer erstellt werden
              // if (contactType === 'Person' && group?.type === 'Haushalt') {
              if (contactType === 'Person') {
                setShowHouseholdDialog(true);
              } else {
                // Default to company dialog for other cases (Organisations or mixed)
                // Or check if contactType is Organisation?
                // User request implies linking household OR company members. 
                // If "Role im Unternehmen" table is shown, open company dialog.
                // This is determined by contactType != 'Person'.
                setShowCompanyDialog(true);
              }
            }}
          />
          <Table
            ref={tableRef1}
            aria-label={contactType === 'Person' ? "Rolle im Haushalt-Tabelle" : "Verantwortlichkeiten und Zuständigkeiten fürs Unternehmen-Tabelle"}
            {...sizing1.getTableProps()}
          >
            <TableHeader>
              <TableRow>
                {columns.map((col) => (
                  <TableHeaderCell
                    key={col.columnId}
                    {...sizing1.getTableHeaderCellProps(
                      col.columnId as string,
                    )}
                  >
                    {col.columnId === "select" ? null : col.renderHeaderCell()}
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows1.map(({ item }, i) => (
                <TableRow
                  key={item.id}
                  className={i % 2 === 1 ? styles.stripedRow : undefined}
                >
                  <TableCell
                    {...sizing1.getTableCellProps("select")}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selectedIds.has(item.id!)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(item.id!)}
                    />
                  </TableCell>
                  <TableCell
                    {...sizing1.getTableCellProps("Name")}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={styles.cellEllipsis} title={item.name}>
                      {item.name}
                    </div>
                  </TableCell>
                  <TableCell
                    {...sizing1.getTableCellProps("Rolle")}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={styles.cellEllipsis} title={item.rolle}>
                      {item.rolle}
                    </div>
                  </TableCell>
                  <TableCell
                    {...sizing1.getTableCellProps("Funktion")}
                    onClick={(e) => e.stopPropagation() /* Prevent navigating when clicking menu */}
                  >
                    {/* Render directly as it might contain interactive menu */}
                    {columns.find(c => c.columnId === "Funktion")?.renderCell(item)}
                  </TableCell>
                  <TableCell
                    {...sizing1.getTableCellProps("Kontaktmöglichkeit")}
                    onClick={() => handleEdit(item)}
                  >
                    {/* Keep buttons interactive */}
                    <div style={{ display: "flex", gap: "8px" }} >
                      <Button
                        icon={<Call20Regular />}
                        appearance="subtle"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDialogState({ open: true, mode: "phone", contact: item as any });
                        }}
                      />
                      <Button
                        icon={<Mail20Regular />}
                        appearance="subtle"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDialogState({ open: true, mode: "email", contact: item as any });
                        }}
                      />
                    </div >
                  </TableCell>
                  <TableCell
                    {...sizing1.getTableCellProps("Verbindungsnotiz")}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={styles.cellEllipsis} title={item.anteile}>
                      {item.anteile}
                    </div>
                  </TableCell>
                  <TableCell
                    {...sizing1.getTableCellProps("Extern")}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={styles.cellEllipsis} title={item.isExtern ? "Ja" : "Nein"}>
                      {item.isExtern ? "Ja" : "Nein"}
                    </div>
                  </TableCell>
                  <TableCell
                    {...sizing1.getTableCellProps("Unternehmen")}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={styles.cellEllipsis} title={item.isExtern ? item.employer : ""}>
                      {item.isExtern ? item.employer : ""}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* "Beziehungen zu Personen" nur für Privatkontakte (Person). Bei
          Firmen-Kontakten ist diese Tabelle nicht sinnvoll — dort gibt es
          stattdessen die Verbindungen-zu-Personen-Tabelle weiter unten. */}
      {contactType === 'Person' && (
      <div style={{ marginTop: 32 }}>
        <Title3>Beziehungen zu Personen</Title3>
        <div style={{ fontSize: "14px", width: "100%", overflowX: "auto", marginTop: 16 }}>
          <CommandBar
            header=""
            onSearchChange={() => { }}
            onEditClick={() => { }}
            onNewClick={() => setShowRelationshipDialog(true)}
          />
          <Table
            ref={tableRef2}
            aria-label="Verknüpfungen-Tabelle"
            {...sizing2.getTableProps()}
          >
            <TableHeader>
              <TableRow>
                {columnsConnections.map((col) => (
                  <TableHeaderCell
                    key={col.columnId}
                    {...sizing2.getTableHeaderCellProps(
                      col.columnId as string,
                    )}
                  >
                    {col.columnId === "select" ? null : col.renderHeaderCell()}
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows2.map(({ item }, i) => (
                <TableRow
                  key={item.id}
                  className={i % 2 === 1 ? styles.stripedRow : undefined}
                >
                  <TableCell
                    {...sizing2.getTableCellProps("select")}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selectedIds.has(item.id!)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(item.id!)}
                    />
                  </TableCell>
                  <TableCell
                    {...sizing2.getTableCellProps("Rolle")}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={styles.cellEllipsis} title={item.rolle}>
                      {item.rolle}
                    </div>
                  </TableCell>
                  <TableCell
                    {...sizing2.getTableCellProps("Funktion")}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {columns.find(c => c.columnId === "Funktion")?.renderCell(item)}
                  </TableCell>
                  <TableCell
                    {...sizing2.getTableCellProps("Name")}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={styles.cellEllipsis} title={item.name}>
                      {item.name}
                    </div>
                  </TableCell>
                  <TableCell
                    {...sizing2.getTableCellProps("Kontaktmöglichkeit")}
                    onClick={() => handleEdit(item)}
                  >
                    <div style={{ display: "flex", gap: "8px" }} >
                      <Button
                        icon={<Call20Regular />}
                        appearance="subtle"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDialogState({ open: true, mode: "phone", contact: item as any });
                        }}
                      />
                      <Button
                        icon={<Mail20Regular />}
                        appearance="subtle"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDialogState({ open: true, mode: "email", contact: item as any });
                        }}
                      />
                      <Button
                        icon={<Delete20Regular />}
                        appearance="subtle"
                        title="Verbindung entfernen"
                        aria-label="Verbindung entfernen"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveRelation(item.id);
                        }}
                      />
                    </div >
                  </TableCell>
                  <TableCell
                    {...sizing2.getTableCellProps("Anteile")}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={styles.cellEllipsis} title={item.anteile}>
                      {item.anteile}
                    </div>
                  </TableCell>
                  <TableCell
                    {...sizing2.getTableCellProps("Extern")}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={styles.cellEllipsis} title={item.isExtern ? "Ja" : "Nein"}>
                      {item.isExtern ? "Ja" : "Nein"}
                    </div>
                  </TableCell>
                  <TableCell
                    {...sizing2.getTableCellProps("Unternehmen")}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={styles.cellEllipsis} title={item.isExtern ? item.employer : ""}>
                      {item.isExtern ? item.employer : ""}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      )}
      <div style={{ marginTop: 22 }}>
        <Title3>Verbindungen zu Firmen</Title3>
        <div
          style={{
            fontSize: "16px",
            color: tokens.colorNeutralForeground3,
            marginTop: 2,
          }}
        >
          Beteiligungen an Firmen, Tochterunternehmen, rechtlich selbstständige Niederlassungen
        </div>
        <div style={{ fontSize: "14px", width: "100%", overflowX: "auto", marginTop: 16 }}>
          <CommandBar
            header=""
            onSearchChange={() => { }}
            onEditClick={() => { }}
            onNewClick={() => setShowRelationshipDialog(true)}
          />
          <Table
            ref={tableRef2}
            aria-label="Verknüpfungen-Tabelle"
            {...sizing2.getTableProps()}
          >
            <TableHeader>
              <TableRow>
                {columnsConnections.map((col) => (
                  <TableHeaderCell
                    key={col.columnId}
                    {...sizing2.getTableHeaderCellProps(
                      col.columnId as string,
                    )}
                  >
                    {col.columnId === "select" ? null : col.renderHeaderCell()}
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows2.map(({ item }, i) => (
                <TableRow
                  key={item.id}
                  className={i % 2 === 1 ? styles.stripedRow : undefined}
                >
                  <TableCell
                    {...sizing2.getTableCellProps("select")}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selectedIds.has(item.id!)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelect(item.id!)}
                    />
                  </TableCell>
                  <TableCell
                    {...sizing2.getTableCellProps("Rolle")}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={styles.cellEllipsis} title={item.rolle}>
                      {item.rolle}
                    </div>
                  </TableCell>
                  <TableCell
                    {...sizing2.getTableCellProps("Funktion")}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {columns.find(c => c.columnId === "Funktion")?.renderCell(item)}
                  </TableCell>
                  <TableCell
                    {...sizing2.getTableCellProps("Name")}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={styles.cellEllipsis} title={item.name}>
                      {item.name}
                    </div>
                  </TableCell>
                  <TableCell
                    {...sizing2.getTableCellProps("Kontaktmöglichkeit")}
                    onClick={() => handleEdit(item)}
                  >
                    <div style={{ display: "flex", gap: "8px" }} >
                      <Button
                        icon={<Call20Regular />}
                        appearance="subtle"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDialogState({ open: true, mode: "phone", contact: item as any });
                        }}
                      />
                      <Button
                        icon={<Mail20Regular />}
                        appearance="subtle"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDialogState({ open: true, mode: "email", contact: item as any });
                        }}
                      />
                      <Button
                        icon={<Delete20Regular />}
                        appearance="subtle"
                        title="Verbindung entfernen"
                        aria-label="Verbindung entfernen"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveRelation(item.id);
                        }}
                      />
                    </div >
                  </TableCell>
                  <TableCell
                    {...sizing2.getTableCellProps("Anteile")}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={styles.cellEllipsis} title={item.anteile}>
                      {item.anteile}
                    </div>
                  </TableCell>
                  <TableCell
                    {...sizing2.getTableCellProps("Extern")}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={styles.cellEllipsis} title={item.isExtern ? "Ja" : "Nein"}>
                      {item.isExtern ? "Ja" : "Nein"}
                    </div>
                  </TableCell>
                  <TableCell
                    {...sizing2.getTableCellProps("Unternehmen")}
                    onClick={() => handleEdit(item)}
                  >
                    <div className={styles.cellEllipsis} title={item.isExtern ? item.employer : ""}>
                      {item.isExtern ? item.employer : ""}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

      </div>

      <div style={{ marginTop: 22 }}>
        <Title3>Kundenverbindungen</Title3>
        {kundenverbindung ? (
          <div
            style={{
              marginTop: 8,
              border: "1px solid #e0e0e0",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <div
              role="button"
              tabIndex={0}
              onClick={() => setKvOpen((o) => !o)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setKvOpen((o) => !o);
              }}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 12px",
                fontWeight: 600,
                background: "#fafafa",
              }}
            >
              <span style={{ width: 12, display: "inline-block" }}>
                {kvOpen ? "▾" : "▸"}
              </span>
              <span>{kundenverbindung.name}</span>
              {kvMembers !== null && (
                <span style={{ fontWeight: 400, color: "#616161" }}>
                  ({kvMembers.length} Kontakt{kvMembers.length === 1 ? "" : "e"})
                </span>
              )}
            </div>
            {kvOpen &&
              (kvLoading ? (
                <div style={{ padding: "10px 12px", color: "#616161" }}>Lädt…</div>
              ) : kvMembers && kvMembers.length > 0 ? (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "#616161" }}>
                      <th style={{ padding: "6px 12px", fontWeight: 600, borderTop: "1px solid #e0e0e0" }}>Name</th>
                      <th style={{ padding: "6px 12px", fontWeight: 600, borderTop: "1px solid #e0e0e0" }}>Typ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kvMembers.map((m, i) => {
                      const isSelf = m.id === kontaktId;
                      return (
                        <tr
                          key={m.id}
                          onClick={() => {
                            if (!isSelf) navigate(`/kontakte/edit/${m.id}`);
                          }}
                          style={{
                            cursor: isSelf ? "default" : "pointer",
                            background: i % 2 === 1 ? "#f5f5f5" : "transparent",
                          }}
                        >
                          <td style={{ padding: "6px 12px" }}>
                            {m.name}
                            {isSelf ? " (dieser Kontakt)" : ""}
                          </td>
                          <td style={{ padding: "6px 12px" }}>{contactTypeLabel(m.typ)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div style={{ padding: "10px 12px", color: "#616161" }}>
                  Keine Kontakte in dieser Kundenverbindung.
                </div>
              ))}
          </div>
        ) : (
          <div style={{ fontSize: "16px", color: "#616161", marginTop: 2 }}>
            Keine Kundenverbindung hinterlegt.
          </div>
        )}
      </div>

    </>
  );
}

export default RelationshipDetails;
